import { Chip, InputBaseProps, InputProps } from "@mui/material";
import Autocomplete, {
  AutocompleteProps,
  AutocompleteRenderInputParams,
} from "@mui/material/Autocomplete";
import { AutocompleteRenderOptionState } from "@mui/material/Autocomplete/Autocomplete";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import { clsx } from "clsx";
import React, {
  ElementType,
  ForwardedRef,
  HTMLAttributes,
  ReactNode,
  useState,
} from "react";
import { IAutocompleteOption } from "src/shared/types/field.types";

export interface IAutocompleteFieldInnerProps<T = string>
  extends Omit<
    AutocompleteProps<
      IAutocompleteOption<T>,
      boolean,
      boolean,
      boolean,
      ElementType<unknown>
    >,
    "renderInput"
  > {
  label?: ReactNode;
  startAdornment?: ReactNode;
  // can add button or icon here, add 'disableClearable = true' if use it
  endAdornment?: ReactNode;
  options: IAutocompleteOption<T>[];
  placeholder?: string;
  inputProps?: InputBaseProps["inputProps"];
  error?: boolean;
  helperText?: ReactNode;
  renderInput?: (params: AutocompleteRenderInputParams) => ReactNode;
  ref?: ForwardedRef<HTMLInputElement>;
  inputRef?: ForwardedRef<HTMLInputElement>;
  renderOption?: (
    props: HTMLAttributes<HTMLLIElement>,
    option: IAutocompleteOption<T>,
    state?: AutocompleteRenderOptionState,
  ) => ReactNode;
  chipAvatar?: (value: IAutocompleteOption<T>) => JSX.Element;
  name?: string;
}

function AutocompleteFieldInner<T = string>({
  label,
  startAdornment,
  endAdornment,
  placeholder,
  inputProps,
  error,
  helperText,
  renderInput,
  inputRef,
  ref,
  renderOption,
  chipAvatar,
  ...selectProps
}: IAutocompleteFieldInnerProps<T>) {
  const [isShrinkLable, setIsShrinkLable] = useState(
    !!selectProps.value || !!selectProps.value?.length,
  );

  const hasStartAdornment = !!startAdornment;
  const renderStartAdornment = (
    props: InputProps,
  ): Record<string, ReactNode> => {
    if (hasStartAdornment) {
      return {
        startAdornment: (
          <>
            <InputAdornment position="start">{startAdornment}</InputAdornment>
            {/* hack to fix behaviour */}
            {props.startAdornment}
          </>
        ),
      };
    } else return {};
  };

  const renderEndAdornment = (props: InputProps): Record<string, ReactNode> => {
    return {
      endAdornment: (
        <React.Fragment>
          {!!endAdornment && (
            <InputAdornment sx={{ right: 32 }} position="end">
              {endAdornment}
            </InputAdornment>
          )}
          {props.endAdornment}
        </React.Fragment>
      ),
    };
  };

  return (
    <Autocomplete
      {...selectProps}
      loadingText="Загрузка..."
      isOptionEqualToValue={(option, value) => {
        if (typeof option === "object" && typeof value === "object") {
          return option.value == value.value;
        }

        if (typeof option === "object") {
          return option.value == value;
        }
        if (typeof value === "object") {
          return value.value == option;
        }

        return option == value;
      }}
      renderTags={
        selectProps.renderTags
          ? selectProps.renderTags
          : (inputValues, getTagProps, ownerState) => {
              return inputValues.map((valueItem, index) => {
                let tagLabel: string | JSX.Element;

                if (typeof valueItem === "string") {
                  const matchedOption = ownerState.options.find(
                    (optionItem) => {
                      if (typeof optionItem === "string") return false;
                      else return optionItem.value == valueItem;
                    },
                  );
                  if (matchedOption !== undefined) {
                    tagLabel = (matchedOption as IAutocompleteOption).name;
                  } else tagLabel = valueItem;

                  return (
                    <Chip
                      {...selectProps.ChipProps}
                      avatar={chipAvatar && chipAvatar(valueItem)}
                      {...getTagProps({ index })}
                      label={tagLabel}
                      key={valueItem}
                    />
                  );
                } else
                  return (
                    <Chip
                      {...selectProps.ChipProps}
                      avatar={chipAvatar && chipAvatar(valueItem)}
                      {...getTagProps({ index })}
                      label={valueItem.name}
                      key={String(valueItem.value)}
                    />
                  );
              });
            }
      }
      getOptionDisabled={(option) => {
        if (typeof option === "string") {
          return false;
        }

        return !!option.disabled;
      }}
      getOptionLabel={(option) => {
        if (typeof option === "string") {
          return option;
        } else return String(option.name);
      }}
      renderOption={
        renderOption ||
        function (props, option) {
          return (
            <Box
              component="li"
              {...props}
              key={String(option.value)}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              {String(option.name)}
            </Box>
          );
        }
      }
      renderInput={(params) => {
        return renderInput ? (
          renderInput(params)
        ) : (
          <TextField
            ref={ref}
            error={error}
            helperText={helperText}
            inputRef={inputRef}
            {...params}
            label={label}
            slotProps={{
              inputLabel: {
                ...params.InputLabelProps,
                shrink: hasStartAdornment ? isShrinkLable : undefined,
                className: clsx({ adornedStart: hasStartAdornment }),
              },
              htmlInput: {
                ...params.inputProps,
                ...inputProps,
                className: clsx(params.inputProps.className, {
                  startIcon: hasStartAdornment,
                }),
              },
              input: {
                onFocus: () => setIsShrinkLable(true),
                onBlur: (e) => {
                  setIsShrinkLable(
                    typeof e.target.value === "object"
                      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (e.target.value as any).length
                      : !!e.target.value,
                  );
                },
                ...params.InputProps,
                ...renderStartAdornment(params.InputProps),
                ...renderEndAdornment(params.InputProps),
              },
            }}
            placeholder={placeholder}
          />
        );
      }}
      slotProps={{
        ...selectProps.slotProps,
        paper: {
          ...selectProps.slotProps?.paper,
          sx: {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            ...selectProps.slotProps?.paper?.sx,
            boxShadow: "0px 6px 12px rgba(0, 0, 0, 0.12)",
            marginTop: "8px",
            marginBottom: "8px",
          },
        },
      }}
    />
  );
}

export default AutocompleteFieldInner;
